import api, { type ApiResponse } from "./api";

export type Category = {
  id: string;
  name: string;
  slug: string;
};

export type SubmitApplicationResponse = {
  application: {
    id: string;
    slug: string;
    status: string;
    publicationWorkflowStatus: string;
    requiresPayment: boolean;
    hasActiveSubscription: boolean;
    publication?: {
      published: boolean;
      status: string;
    };
  };
};

export async function fetchCategories(): Promise<Category[]> {
  const response = await api.get<ApiResponse<{ categories: Category[] }>>("/categories");
  return response.data.data.categories;
}

export async function acceptPublisherAgreement(
  agreementVersion: string,
): Promise<void> {
  await api.post("/publisher/agreement/accept", { agreementVersion });
}

export async function submitApplication(formData: FormData): Promise<SubmitApplicationResponse> {
  const response = await api.post<ApiResponse<SubmitApplicationResponse>>("/apps", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data.data;
}

export async function fetchPublisherAgreementVersion(): Promise<string> {
  const response = await api.get<
    ApiResponse<{ document: { version: string } }>
  >("/legal/publisher-agreement");
  return response.data.data.document.version;
}

export type PublisherAppEditModel = {
  id: string;
  name: string;
  slug: string;
  status: string;
  category_id: string | null;
  category_name: string | null;
  problem_solved: string | null;
  short_description: string | null;
  developer_website: string | null;
  apps_for_sale: boolean;
  current_version: string | null;
  icon_url: string | null;
  screenshots: Array<{
    url: string;
    file_name: string;
    display_order: number;
  }>;
};

export async function fetchPublisherAppForEdit(
  applicationId: string,
): Promise<PublisherAppEditModel> {
  const response = await api.get<ApiResponse<{ app: PublisherAppEditModel }>>(
    `/publisher/apps/${applicationId}`,
  );
  return response.data.data.app;
}

export async function updatePublisherApp(
  applicationId: string,
  formData: FormData,
): Promise<PublisherAppEditModel> {
  const response = await api.patch<ApiResponse<{ app: PublisherAppEditModel }>>(
    `/publisher/apps/${applicationId}`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    },
  );
  return response.data.data.app;
}

export type PublisherAppUpdateContext = {
  id: string;
  name: string;
  slug: string;
  status: string;
  current_version_id: string | null;
  current_version: string | null;
  security_status: string;
  public_release_allowed: boolean;
};

export type ApplicationVersionHistoryItem = {
  id: string;
  version_number: string;
  is_current: boolean;
  submitted_at: string | null;
  approved_at: string | null;
  security_status: string | null;
};

export type VersionUpdateResult = {
  applicationId: string;
  versionId: string;
  versionNumber: string;
  activated: boolean;
  securityStatus: string;
  message: string;
};

export async function fetchPublisherAppUpdateContext(
  applicationId: string,
): Promise<{
  app: PublisherAppUpdateContext;
  versions: ApplicationVersionHistoryItem[];
}> {
  const response = await api.get<
    ApiResponse<{
      app: PublisherAppUpdateContext;
      versions: ApplicationVersionHistoryItem[];
    }>
  >(`/publisher/apps/${applicationId}/update`);
  return response.data.data;
}

export async function submitApplicationVersionUpdate(
  applicationId: string,
  formData: FormData,
): Promise<VersionUpdateResult> {
  const response = await api.post<ApiResponse<{ update: VersionUpdateResult }>>(
    `/publisher/apps/${applicationId}/update`,
    formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    },
  );
  return response.data.data.update;
}
